import { Alert } from '@mm/components';
import { MAX_TEXT_WIDGET_LENGTH } from '@mm/constants/widgets';
import { useResolutionInfo } from '@mm/hooks';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { noop } from 'lodash';
import React, {
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import {
  ActivePathSelection,
  ActivePathType,
} from '../common/ActivePathSelection/ActivePathSelection';
import { BubbleTextInput } from '../common/BubbleTextInput/BubbleTextInput';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { PathGraphContext } from '../common/PathGraph/PathGraph';
import { useEditMode } from '../common/use-edit-mode';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import { OptInButtonStack } from './OptInButtonStack';
import { OptInCreateButton } from './OptInCreateButton';
import './OptInWidget.pcss';

type Props = {
  widget: WidgetModel;
  draggableProvided?: DraggableProvided;
  showAttributePicker?: boolean;
  showAttributesStartWarning?: boolean;
  showCountByChannel?: boolean;
  forInstagram?: boolean;
  widgetsToExclude?: WidgetTypes[];
  platforms?: PlatformType[];
  usedInSlideout?: boolean;
} & WidgetContainerRefProp;

const OPT_IN_BUTTONS_LIMIT = 5;

const AddButton = ({
  onClick,
  disabled,
}: {
  onClick?: (e?: MouseEvent<HTMLDivElement>) => void;
  disabled?: boolean;
}) => {
  return (
    <div
      className={classnames('opt-in-widget__add-button', {
        'opt-in-widget__add-button--disabled': disabled,
      })}
      onClick={disabled ? noop : onClick}
    >
      + Add Button
    </div>
  );
};

export const OptInWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    forInstagram,
    showAttributePicker = true,
    showAttributesStartWarning = false,
    showCountByChannel,
    widgetsToExclude,
    platforms,
    usedInSlideout,
  } = props;
  const chatbotId = useSelector(activeChatbotIdSelector);
  const commonWidgetHandlers = useCommonWidgetHandlers();

  const { editMode, setEditMode } = useEditMode(props);
  const [showCreateButton, setShowCreateButton] = useState(false);
  const [openedButtonId, setOpenedButtonId] = useState<number | undefined>();
  const [activePageId, setActivePageId] = useState<number | undefined>();
  const [activePathType, setActivePathType] =
    useState<ActivePathType>('regular');
  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);

  const {
    getActiveButtonFromWidgetButtons,
    removeNodeAndSuccessors,
    deselectWidgetButtons,
    setActiveNode,
  } = useContext(PathGraphContext);

  const { isDesktop } = useResolutionInfo();

  const activeButton = useMemo(() => {
    return getActiveButtonFromWidgetButtons?.(widget.buttons);
  }, [getActiveButtonFromWidgetButtons, widget]);

  const beforeDestroy = useCallback(() => {
    if (activeButton) {
      setActiveNode?.({
        id: activeButton.id,
        type: 'button',
        action: 'close',
      });
    }
    removeNodeAndSuccessors?.({
      id: widget.id,
      type: 'widget',
      actionType: 'delete',
      parentId: widget.pageId,
      parentType: 'dialogue',
    });
  }, [widget.id, activeButton?.id]);

  const onClickAddNew = async () => {
    setShowCreateButton(true);
    deselectWidgetButtons?.(widget.buttons);
    setOpenedButtonId(undefined);
  };
  const onTextInputClick = () => setEditMode(true);

  useEffect(() => {
    if (widget.config.body) {
      setHasAttributesInBody(/{{.+}}/.test(widget.config.body));
    } else {
      setHasAttributesInBody(false);
    }
  }, [widget.config.body]);

  return (
    <div className="opt-in-widget">
      <div className="opt-in-widget__main-area-wrapper">
        <FacebookPageIcon />

        <div className="opt-in-widget__right-area">
          <div className="opt-in-widget__right-top-area">
            <OnClickElsewhere
              className="opt-in-widget__text-area-wrapper"
              handleClick={() => setEditMode(false)}
            >
              <BubbleTextInput
                className="opt-in-widget__bubble-text-input"
                updateText={newText =>
                  commonWidgetHandlers.updateBodyText(widget, newText)
                }
                value={widget.config.body}
                editMode={editMode}
                onClick={onTextInputClick}
                emojiPickerColor="#9a9aa2"
                attributePickerColor="#9a9aa2"
                showAttributePicker={showAttributePicker}
                maxLength={MAX_TEXT_WIDGET_LENGTH}
                maxSmsChannelTextLength={MAX_TEXT_WIDGET_LENGTH}
                maxOthersChannelTextLength={MAX_TEXT_WIDGET_LENGTH}
                showCountByChannel={showCountByChannel}
                newCharacterCountDesign
              />
            </OnClickElsewhere>

            <WidgetControls
              widget={widget}
              draggableProvided={draggableProvided}
              beforeDestroy={beforeDestroy}
              platforms={platforms}
            />

            {forInstagram && !widget.supportedByPlatforms.instagram && (
              <ErrorPortal widget={widget}>
                <WidgetUnsupportedError isError type="instagram" />
              </ErrorPortal>
            )}

            {hasAttributesInBody &&
              showAttributesStartWarning &&
              !showAttributePicker && (
                <ErrorPortal widget={widget}>
                  <div className="text-widget__alert-container">
                    <Alert
                      className="text-widget__warning"
                      type={'error'}
                      showCloseIcon={false}
                    >
                      Attributes are not available on the first widget since we
                      only get contact’s profile AFTER their first response.
                    </Alert>
                  </div>
                </ErrorPortal>
              )}
          </div>

          <div className="opt-in-widget__buttons-area">
            <OptInButtonStack
              widget={widget}
              openedButtonId={openedButtonId}
              setActivePageId={setActivePageId}
              setOpenedButtonId={(id: number) => {
                setOpenedButtonId(id);
                setShowCreateButton(false);
              }}
              setActivePathType={setActivePathType}
            />
            {showCreateButton && (
              <OptInCreateButton
                widget={widget}
                closeButtonArea={() => setShowCreateButton(false)}
                onCreate={button => {
                  setOpenedButtonId(button.id);
                  setActivePageId(button.config.pageId || undefined);
                }}
              />
            )}
            <AddButton
              onClick={onClickAddNew}
              disabled={widget.buttons.length >= OPT_IN_BUTTONS_LIMIT}
            />
          </div>
        </div>
      </div>
      {activeButton && (
        <ActivePathSelection
          chatbotId={chatbotId}
          widget={widget}
          dialogueId={activePageId}
          activeButton={activeButton}
          activePathType={activePathType}
          widgetsToExclude={widgetsToExclude}
          platforms={platforms}
          usedInSlideout={usedInSlideout}
        />
      )}
    </div>
  );
};
